import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Chip,
  Divider,
  useMediaQuery
} from '@mui/material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { getTransferOptions, transferUserDataAndDelete } from '../services/api';
import { useTheme } from '@mui/material/styles';

export default function UserTransferDialog({
  open,
  onClose,
  userToDelete,
  references,
  onSuccess
}) {
  const [selectedUserId, setSelectedUserId] = useState('');
  const queryClient = useQueryClient();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  // Fetch available users for transfer
  const {
    data: transferOptions = [],
    isLoading: isLoadingOptions,
    error: optionsError
  } = useQuery({
    queryKey: ['transferOptions', userToDelete?.id],
    queryFn: () => getTransferOptions(userToDelete?.id),
    enabled: open && !!userToDelete?.id,
    staleTime: 0
  });

  // Transfer and delete mutation
  const transferMutation = useMutation({
    mutationFn: ({ userId, transferToUserId }) =>
      transferUserDataAndDelete(userId, transferToUserId),
    onSuccess: (data) => {
      toast.success(data.message || 'User data transferred and deleted successfully');
      queryClient.invalidateQueries({ queryKey: ['users'] });
      onSuccess();
      onClose();
    },
    onError: (error) => {
      toast.error(`Failed to transfer user data: ${error.response?.data?.message || error.message}`);
    }
  });

  const handleTransfer = () => {
    if (!selectedUserId) {
      toast.error('Please select a user to transfer data to');
      return;
    }

    transferMutation.mutate({
      userId: userToDelete.id,
      transferToUserId: selectedUserId
    });
  };

  const handleClose = () => {
    if (!transferMutation.isPending) {
      setSelectedUserId('');
      onClose();
    }
  };

  // Reset selected user when dialog opens/closes
  useEffect(() => {
    if (!open) {
      setSelectedUserId('');
    }
  }, [open]);

  const getReferenceCount = () => {
    return (references?.shopify_config?.length || 0) +
           (references?.erp_config?.length || 0) +
           (references?.schedulers?.length || 0);
  };

  const renderReferences = () => {
    const items = [];

    if (references?.shopify_config?.length > 0) {
      items.push(
        <Box key="shopify" sx={{ mb: 2 }}>
          <Typography variant="subtitle2" color="primary" sx={{ mb: 1 }}>
            Shopify Configurations ({references.shopify_config.length})
          </Typography>
          {references.shopify_config.map((config) => (
            <Chip
              key={config.id}
              label={`${config.store_name || config.store_url}`}
              size="small"
              sx={{ mr: 1, mb: 1 }}
            />
          ))}
        </Box>
      );
    }

    if (references?.erp_config?.length > 0) {
      items.push(
        <Box key="erp" sx={{ mb: 2 }}>
          <Typography variant="subtitle2" color="primary" sx={{ mb: 1 }}>
            ERP Configurations ({references.erp_config.length})
          </Typography>
          {references.erp_config.map((config) => (
            <Chip
              key={config.id}
              label={`${config.erp_name} - ${config.api_base_url}`}
              size="small"
              sx={{ mr: 1, mb: 1 }}
            />
          ))}
        </Box>
      );
    }

    if (references?.schedulers?.length > 0) {
      items.push(
        <Box key="schedulers" sx={{ mb: 2 }}>
          <Typography variant="subtitle2" color="primary" sx={{ mb: 1 }}>
            Schedulers ({references.schedulers.length})
          </Typography>
          {references.schedulers.map((scheduler) => (
            <Chip
              key={scheduler.id}
              label={`${scheduler.name} - ${scheduler.description || 'No description'}`}
              size="small"
              sx={{ mr: 1, mb: 1 }}
            />
          ))}
        </Box>
      );
    }

    return items;
  };

  return (
    <Dialog
      open={open}
      onClose={transferMutation.isPending ? undefined : handleClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
    >
      <DialogTitle>
        Transfer User Data
      </DialogTitle>

      <DialogContent dividers>
        <Alert severity="warning" sx={{ mb: 3 }}>
          <Typography variant="body2">
            The user <strong>{userToDelete?.firstName} {userToDelete?.lastName}</strong> ({userToDelete?.email})
            has {getReferenceCount()} data reference(s) that need to be transferred to another user before deletion.
          </Typography>
        </Alert>

        <Typography variant="h6" sx={{ mb: 2 }}>
          Data References:
        </Typography>

        <Box sx={{ mb: 3 }}>
          {renderReferences()}
        </Box>

        <Divider sx={{ my: 2 }} />

        <Typography variant="h6" sx={{ mb: 2 }}>
          Select Admin User to Transfer Data To:
        </Typography>

        {isLoadingOptions ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={24} />
          </Box>
        ) : optionsError ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            Failed to load available admin users: {optionsError.message}
          </Alert>
        ) : transferOptions.length === 0 ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            No other admin users available for data transfer. Please create another admin account first.
          </Alert>
        ) : (
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Select User</InputLabel>
            <Select
              value={selectedUserId}
              onChange={(e) => setSelectedUserId(e.target.value)}
              label="Select User"
              disabled={transferMutation.isPending}
            >
              {transferOptions.map((user) => (
                <MenuItem key={user.id} value={user.id}>
                  {user.first_name} {user.last_name} ({user.email}) - {user.role}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        <Alert severity="info" sx={{ mt: 2 }}>
          <Typography variant="body2">
            All data references (configurations and schedulers) will be transferred to the selected admin user.
            Data can only be transferred to admin users. This action cannot be undone.
          </Typography>
        </Alert>
      </DialogContent>

      <DialogActions>
        <Button
          onClick={handleClose}
          disabled={transferMutation.isPending}
        >
          Cancel
        </Button>
        <Button
          onClick={handleTransfer}
          variant="contained"
          color="error"
          disabled={!selectedUserId || transferMutation.isPending || transferOptions.length === 0}
          startIcon={transferMutation.isPending ? <CircularProgress size={16} /> : null}
        >
          {transferMutation.isPending ? 'Transferring...' : 'Transfer & Delete'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
